import { DeleteFilled, EditOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Space, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { Column, Table } from 'components';
import {
  StyledTableHeader,
  StyledTableWrapper,
} from 'components/table/table.styled';
import dayjs from 'dayjs';
import { usePermissions } from 'features/users';
import { UserFilterFormValuesPaginated } from 'features/users/components/users-filter-form';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { getRoute } from 'routes/utils';
import { newClients } from 'types/new-clients';

type ClientsTableProps = {
  isFetching: boolean;
  dataList: newClients[] | undefined;
  tableParams: UserFilterFormValuesPaginated;
  updateParams: Dispatch<SetStateAction<UserFilterFormValuesPaginated>>;
};

export const ClientsTable = ({
  dataList,
  isFetching,
  tableParams,
  updateParams,
}: ClientsTableProps) => {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const navigate = useNavigate();
  if (!permissions) return null;
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<newClients> | SorterResult<newClients>[],
  ) => {
    updateParams({
      ...tableParams,
      PageIndex: pagination.current,
      RecordPerPage: pagination.pageSize,
      order_by: Array.isArray(sorter)
        ? undefined
        : sorter.order === 'ascend'
        ? 'name'
        : '-name',
    });
  };

  return (
    <StyledTableWrapper>
      <StyledTableHeader></StyledTableHeader>
      <Table<newClients>
        loading={isFetching}
        dataSource={dataList}
        rowKey='id'
        onChange={handleTableChange}
        pagination={{
          current: tableParams.PageIndex,
          defaultPageSize: tableParams.RecordPerPage,
          total: 10,
        }}
        onRow={(record: any) => {
          return {
            onClick: () => {
              navigate(getRoute(ROUTES.CLIENTS_DETAILS, record.Id));
            },
          };
        }}
        components={{
          header: {
            cell: (props: React.HTMLAttributes<HTMLTableHeaderCellElement>) => (
              <th {...props} />
            ),
          },
        }}
      >
        <Column<newClients>
          title={'Sr No'}
          dataIndex={'Sr No'}
          render={(_, record, index) => index + 1}
        />
        <Column<newClients>
          title={t('assignedClient.table.clientName')}
          dataIndex={'Name'}
        />
        <Column<newClients>
          title={t('assignedClient.table.clientEmail')}
          dataIndex={'Email'}
        />
        <Column<newClients> title={'Phone Number'} dataIndex={'PhoneNumber'} />
        <Column<newClients>
          title={t('assignedClient.table.businessName')}
          dataIndex={'BusinessName'}
        />
        <Column<newClients>
          title={t('assignedClient.table.LastUpdate')}
          dataIndex={'LastUpdated'}
          render={(_, record) =>
            dayjs(record.LastUpdated).format('DD MMM YYYY')
          }
        />
        <Column<newClients>
          title={t('assignedClient.table.status')}
          dataIndex={'DocumentStatus'}
        />
        <Column<newClients>
          title={t('assignedClient.table.action')}
          dataIndex={'user'}
          render={() => (
            <Space>
              <SendOutlined style={{ transform: 'scaleY(-1)' }} />
              <Button
                type='link'
                color='default'
                style={{ backgroundColor: '#223666' }}
                icon={<EditOutlined />}
              />
              <Button danger icon={<DeleteFilled />} />
            </Space>
          )}
        />
      </Table>
    </StyledTableWrapper>
  );
};
